import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { Box, Divider, Grid, IconButton, Typography } from '@mui/material';
import { FileDownloadOutlined, InfoOutlined } from '@mui/icons-material';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';

import { claimsInvoiceActions } from '../../store';
import { formatPhoneNum } from '../../utils/common';
import { FORTA_BLUE, FORTA_ORANGE, FORTA_GRAY3, FORTA_GRAY1 } from '../../utils/colors';
import {
  FHButton,
  FHConfirmDialog,
  FHSimpleTable,
  FHTooltip,
  FProgressBar,
} from '../../components/base';
import PageContainer from '../../components/layout/PageContainer';
import {
  getInvoiceTableTheme,
  getInvoiceTableColumns,
  getInvoiceClaimDetailsTableOptions,
  getInvoiceReadOnlyPaymentTableColumns,
  getInvoiceReadOnlyPaymentsOptions,
  paymentStatusConfig,
} from '../../components/layout/billings/invoices/config';
import {
  calculateClaimsAndPaymentTotal,
  transformInvoiceClaimDetailsTableData,
} from '../../components/layout/billings/invoices/utils';
import { convertDateTimeToCTTime, history, usDollarFormatter } from '../../helpers';
import { DATE_DISPLAY_FORMAT, InvoicePaymentStatus, InvoiceStatus, Themes } from '../../constants';
import { InvoicePaymentStatusEnum } from '../../components/layout/billings/claim-info/claim-payments/constants';

import type { RootState } from '../../store';

type InvoicePaymentStatusType = InvoicePaymentStatus | null;

function InvoicePayment() {
  const { t } = useTranslation('invoice');
  const [searchParams, setSearchParams] = useSearchParams();
  const [isMakingPayment, setIsMakingPayments] = useState<boolean>(false);
  const { invoiceId } = useParams();
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();

  const {
    current: invoiceData,
    loading: invoiceLoading,
    pdfDownloading,
  } = useSelector((state: RootState) => state.claimsInvoice);

  const [showPaymentStatus, setShowPaymentStatus] = useState<InvoicePaymentStatusType>(null);
  const config = showPaymentStatus ? paymentStatusConfig(invoiceId)[showPaymentStatus] : null;

  useEffect(() => {
    const paymentStatus = searchParams.get('payment_status');
    if (!paymentStatus) return;

    setShowPaymentStatus(
      Object.values(InvoicePaymentStatus).find((status) => status === paymentStatus) ?? null,
    );
  }, [searchParams.get('payment_status')]);

  useEffect(() => {
    const checkoutSessionId = searchParams.get('checkout_session_id');
    if (!checkoutSessionId || !invoiceData) return;

    const paymentStatus = Object.entries(invoiceData.checkout_session_map).find(
      ([sessionId]) => sessionId === checkoutSessionId,
    )?.[1].payment_status;

    if (paymentStatus) {
      setShowPaymentStatus(paymentStatus);
    } else {
      console.error(`Payment status not found for checkoutSessionId: ${checkoutSessionId}`);
    }
  }, [searchParams, invoiceData]);

  useEffect(() => {
    if (!invoiceId) return;
    dispatch(
      claimsInvoiceActions.getInvoiceById({
        getAccessTokenSilently,
        invoiceId,
        expand: ['payments', 'checkout_session_map'],
      }),
    );
  }, [invoiceId]);

  const [claimTotals, paymentTotal] = useMemo(
    () => calculateClaimsAndPaymentTotal(invoiceData),
    [invoiceData],
  );

  const handlePDFDownload = async () => {
    const response = await dispatch(
      claimsInvoiceActions.downloadInvoicePDF({
        invoiceId,
        getAccessTokenSilently,
      }),
    );

    if (response.payload?.document_url) {
      window.location.href = response.payload?.document_url;
    }
  };
  const handleInvoicePayment = async () => {
    if (!invoiceId) return;

    setIsMakingPayments(true);
    const response = await dispatch(
      claimsInvoiceActions.makeInvoicePayment({ getAccessTokenSilently, invoiceId }),
    );
    if (response.payload && !response.error) {
      if (response.payload.url) {
        window.open(response.payload.url, '_self');
      }
    } else {
      // Only need to disabled loading on error as on successful response of /pay
      // user is expected to be redirect to the payment page.
      setIsMakingPayments(false);
    }
  };

  const fillteredInvoicePayments =
    invoiceData?.payments?.filter(
      (payment) => payment.status === InvoicePaymentStatusEnum.SUCCESS,
    ) ?? [];

  return (
    <PageContainer sx={{ py: 3 }}>
      {invoiceLoading || isMakingPayment ? (
        <FProgressBar />
      ) : (
        <Box>
          {pdfDownloading && <FProgressBar />}
          <Box display="flex" justifyContent="flex-start" alignItems="center" mb={3} gap={1}>
            <Typography
              color={FORTA_GRAY3}
              fontWeight={700}
              onClick={() => history.navigate('/billings/invoice-list')}
              sx={{ cursor: 'pointer' }}
            >
              {t('invoiceList')} /
            </Typography>
            <Typography color={FORTA_BLUE.primary} fontWeight={700} sx={{ cursor: 'pointer' }}>
              {t('invoice')}
            </Typography>
          </Box>
          <Grid container textAlign="left" gap={2}>
            <Grid item sm={4} xs={6} md={2.25}>
              <Box>
                <Typography color={FORTA_BLUE.primary} fontWeight={700} mb={1.5}>
                  {t('billTo')}
                </Typography>
                <Typography fontWeight={700} mb={1}>
                  {invoiceData?.payee?.first_name + ' ' + invoiceData?.payee?.last_name}
                </Typography>
                {invoiceData?.payee?.street_address_line_one && (
                  <Typography>{invoiceData?.payee?.street_address_line_one}</Typography>
                )}
                {invoiceData?.payee?.street_address_line_two && (
                  <Typography>{invoiceData?.payee?.street_address_line_two}</Typography>
                )}
                <Box>
                  <Typography display="inline">
                    {invoiceData?.payee?.street_address_city ?? ''}
                  </Typography>
                  <Typography display="inline">
                    {`, ${invoiceData?.payee?.street_address_state ?? ''} `}
                  </Typography>
                  <Typography display="inline">
                    {invoiceData?.payee?.street_address_zip ?? ''}
                  </Typography>
                </Box>
                {invoiceData?.payee?.phone && (
                  <Typography>{formatPhoneNum(invoiceData?.payee?.phone)}</Typography>
                )}
              </Box>
            </Grid>
            <Grid item sm={4} xs={6} md={2.25}>
              <Box>
                <Typography color={FORTA_BLUE.primary} fontWeight={700} mb={1.5}>
                  {t('client')}
                </Typography>
                <Typography fontWeight={700} mb={1}>
                  {invoiceData?.client?.first_name + ' ' + invoiceData?.client?.last_name}
                </Typography>
                {invoiceData?.client?.street_address_line_one && (
                  <Typography>{invoiceData?.client?.street_address_line_one}</Typography>
                )}
                {invoiceData?.client?.street_address_line_two && (
                  <Typography>{invoiceData?.client?.street_address_line_two}</Typography>
                )}
                <Box>
                  <Typography display="inline">
                    {invoiceData?.client?.street_address_city ?? ''}
                  </Typography>
                  <Typography display="inline">
                    {`, ${invoiceData?.client?.street_address_state ?? ''} `}
                  </Typography>
                  <Typography display="inline">
                    {invoiceData?.client?.street_address_zip ?? ''}
                  </Typography>
                </Box>
                {invoiceData?.client?.phone && (
                  <Typography>{formatPhoneNum(invoiceData?.client?.phone)}</Typography>
                )}
              </Box>
            </Grid>
            <Grid item sm={4} xs={6} md={1.5} mr={2}>
              <Typography color={FORTA_BLUE.primary} fontWeight={700} mb={1.5}>
                {t('billingProvider')}
              </Typography>
              <Typography>{invoiceData?.billing_facility}</Typography>
            </Grid>
            <Grid item sm={4} xs={6} md={1.25}>
              <Typography color={FORTA_BLUE.primary} fontWeight={700} mb={1.5}>
                {t('invoiceDate')}
              </Typography>
              <Typography>
                {convertDateTimeToCTTime(invoiceData?.date_generated ?? '').format(
                  DATE_DISPLAY_FORMAT,
                )}
              </Typography>
            </Grid>
            <Grid item sm={4} xs={6} md={1.25}>
              <Typography color={FORTA_BLUE.primary} fontWeight={700} mb={1.5}>
                {t('dueDate')}
              </Typography>
              <Typography>{moment(invoiceData?.date_due).format('MM/DD/YY')}</Typography>
            </Grid>
            <Grid item sm={4} xs={6} md={2.5} width={'100%'}>
              {invoiceData?.status === InvoiceStatus.PAID ? (
                <>
                  <Typography
                    color={FORTA_BLUE.primary}
                    fontWeight={700}
                    fontSize={22}
                    mb={2}
                    textAlign={'center'}
                  >
                    {t('paid')}
                  </Typography>
                  <FHButton
                    fullWidth
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handlePDFDownload}
                  >
                    {t('downloadPDF')}
                  </FHButton>
                </>
              ) : (
                <>
                  <Typography color={FORTA_BLUE.primary} fontWeight={700} fontSize={20} mb={2}>
                    {t('amountDue')}: {usDollarFormatter.format(invoiceData?.amount_due ?? 0)}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={2}>
                    {Object.values(invoiceData?.checkout_session_map ?? {}).some(
                      (session) => session.payment_status === InvoicePaymentStatus.PROCESSING,
                    ) ? (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          border: `1px solid ${FORTA_ORANGE}`,
                          borderRadius: '16px',
                          backgroundColor: '#fff3e0',
                          padding: '0.75rem 0.5rem',
                          gap: '0.5rem',
                        }}
                      >
                        <Typography color={FORTA_ORANGE} fontWeight={700} fontSize={15}>
                          Processing payment
                        </Typography>
                        <FHTooltip
                          arrow
                          title={'Transaction is processing, please check again later.'}
                          tooltipBorder=" 1px solid #FFD54F"
                          sx={{
                            borderRadius: '10px',
                            display: 'flex',
                            paddingRight: '1rem',
                            maxWidth: '300px',
                          }}
                          tooltipbackground={FORTA_GRAY1}
                          tooltipBoxShadow={'0px 4px 4px rgba(0, 0, 0, 0.25)'}
                        >
                          <InfoOutlined sx={{ color: FORTA_ORANGE }} />
                        </FHTooltip>
                      </Box>
                    ) : (
                      <FHButton
                        fullWidth
                        onClick={handleInvoicePayment}
                        variant="contained"
                        color="primary"
                        size="small"
                        data-testid="makePaymentButton"
                      >
                        {t('makePayment')}
                      </FHButton>
                    )}
                    <IconButton>
                      <FileDownloadOutlined
                        sx={{ color: FORTA_BLUE.primary }}
                        onClick={handlePDFDownload}
                        data-testid="downloadPdfButton"
                      />
                    </IconButton>
                  </Box>
                </>
              )}
            </Grid>
          </Grid>
          <Divider sx={{ mt: 4 }} />
          <Typography textAlign="left" fontWeight={700} mb={1} sx={{ mt: 3 }}>
            {t('servicesIncluded')}
          </Typography>
          <FHSimpleTable
            data={transformInvoiceClaimDetailsTableData(invoiceData?.claims ?? [])}
            columnDefinitions={getInvoiceTableColumns(t)}
            columnOptions={getInvoiceClaimDetailsTableOptions(
              claimTotals,
              invoiceData?.is_any_claim_paid_to_subscriber ?? false,
            )}
            tableTheme={getInvoiceTableTheme(Themes.Primary)}
          />
          {fillteredInvoicePayments.length > 0 && (
            <>
              <Typography textAlign="left" fontWeight={700} mb={1} mt={3}>
                {t('paymentsReceivedForThisInvoice')}
              </Typography>
              <FHSimpleTable
                data={fillteredInvoicePayments}
                columnDefinitions={getInvoiceReadOnlyPaymentTableColumns(t)}
                columnOptions={getInvoiceReadOnlyPaymentsOptions(paymentTotal)}
                tableTheme={getInvoiceTableTheme(Themes.Primary)}
              />
            </>
          )}
        </Box>
      )}
      {invoiceId && (
        <FHConfirmDialog
          title={config?.title ?? ''}
          titleStyles={{
            color: config?.color ?? FORTA_BLUE.primary,
          }}
          open={Boolean(showPaymentStatus)}
          contentRenderer={() => (
            <Box px={1} py={5}>
              <Typography fontSize={21} textAlign={'center'} lineHeight={1.8}>
                {config?.message ?? ''}
              </Typography>
            </Box>
          )}
          submittext={config?.submitText ?? 'Okay'}
          canceltext="Cancel"
          hideCancelButton={config?.hideCancelButton ?? false}
          onClose={(_, reason) => {
            if (reason && reason === 'backdropClick') return;
            setShowPaymentStatus(null);
            setSearchParams();
          }}
          onSubmit={() => {
            setShowPaymentStatus(null);
            setSearchParams();
          }}
        />
      )}
    </PageContainer>
  );
}

export default InvoicePayment;
